import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Modal, Button, Badge, ListGroup, Tab, Tabs } from 'react-bootstrap';

// Setup moment localizer
const localizer = momentLocalizer(moment);

const Calendarview = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('calendar'); 
  const [eventFilter, setEventFilter] = useState('all'); 

  useEffect(() => {
    const demoEvents = [
      {
        id: 1,
        title: 'Orientation Day',
        start: new Date(2025, 8, 1, 9, 0),
        end: new Date(2025, 8, 1, 12, 0),
        type: 'event',
        description: 'Welcome session for new students',
        location: 'Main Auditorium'
      },
      {
        id: 2,
        title: 'Sports Day',
        start: new Date(2025, 8, 15),
        end: new Date(2025, 8, 15),
        type: 'event',
        allDay: true,
        description: 'Annual inter-house sports competition'
      },
      {
        id: 3,
        title: 'Mid-Term Exams',
        start: new Date(2025, 8, 25, 9, 0),
        end: new Date(2025, 8, 29, 12, 0),
        type: 'exam',
        description: 'Mid-term examinations for all classes'
      },
      {
        id: 4,
        title: 'Gandhi Jayanti',
        start: new Date(2025, 9, 2),
        end: new Date(2025, 9, 2),
        type: 'holiday',
        allDay: true,
        description: 'School will remain closed'
      },
      {
        id: 5,
        title: 'Parent-Teacher Meeting',
        start: new Date(2025, 9, 10, 14, 0),
        end: new Date(2025, 9, 10, 17, 0),
        type: 'event',
        description: 'Discussion of student progress',
        location: 'Classrooms'
      }
    ];
    setEvents(demoEvents);
  }, []);

  useEffect(() => {
    updateSelectedDateEvents(currentDate);
  }, [currentDate, events]);

  const updateSelectedDateEvents = (date) => {
    const dayStart = moment(date).startOf('day').toDate();
    const dayEnd = moment(date).endOf('day').toDate();
    
    const filteredEvents = events.filter(event => {
      return (
        (event.start >= dayStart && event.start <= dayEnd) ||
        (event.end >= dayStart && event.end <= dayEnd) ||
        (event.start <= dayStart && event.end >= dayEnd)
      );
    });
    
    setSelectedDateEvents(filteredEvents);
  };

  const getFilteredEvents = () => {
    const now = new Date();
    switch (eventFilter) {
      case 'upcoming':
        return events.filter(event => event.start > now);
      case 'finished':
        return events.filter(event => event.end < now);
      default:
        return events;
    }
  };

  const handleSelectSlot = (slotInfo) => {
    setCurrentDate(slotInfo.start);
    updateSelectedDateEvents(slotInfo.start);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '';
    switch (event.type) {
      case 'holiday':
        backgroundColor = '#dc3545'; 
        break;
      case 'exam':
        backgroundColor = '#ffc107';
        break;
      default:
        backgroundColor = '#0d6efd';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        padding: '2px 5px'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleNavigate = (date, view) => {
    setCurrentDate(date);
    if (view) setView(view);
  };

  const handleViewChange = (view) => {
    setView(view);
  };

  const goToPrevious = () => {
    let newDate;
    if (view === 'month') {
      newDate = moment(currentDate).subtract(1, 'month').toDate();
    } else if (view === 'week') {
      newDate = moment(currentDate).subtract(1, 'week').toDate();
    } else {
      newDate = moment(currentDate).subtract(1, 'day').toDate();
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    let newDate;
    if (view === 'month') {
      newDate = moment(currentDate).add(1, 'month').toDate();
    } else if (view === 'week') {
      newDate = moment(currentDate).add(1, 'week').toDate();
    } else {
      newDate = moment(currentDate).add(1, 'day').toDate();
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="school-calendar p-3">
      <div className="calendar-header mb-4">
        <h2 className="h2  display-5 fw-bold text-center">School Academic Calendar</h2>
        
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="calendar" title="Calendar View">
          </Tab>
          <Tab eventKey="list" title="Event List">
          </Tab>
        </Tabs>

        {activeTab === 'calendar' && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="view-options">
              <Button
                variant={view === 'month' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleViewChange('month')}
                className="me-2"
              >
                Month
              </Button>
              <Button
                variant={view === 'week' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleViewChange('week')}
                className="me-2"
              >
                Week
              </Button>
              <Button
                variant={view === 'day' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleViewChange('day')}
              >
                Day
              </Button>
            </div>
            <div className="d-flex flex-column align-items-center gap-2">
              <h4 className="mb-0">{moment(currentDate).format('MMMM YYYY')}</h4>
              
              <div className="d-flex align-items-center gap-2">
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={goToPrevious}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-chevron-left me-1"></i>
                  Previous
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={goToToday}
                >
                  Today
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={goToNext}
                  className="d-flex align-items-center"
                >
                  Next
                  <i className="bi bi-chevron-right ms-1"></i>
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'list' && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="filter-options">
              <Button
                variant={eventFilter === 'all' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setEventFilter('all')}
                className="me-2"
              >
                All Events
              </Button>
              <Button
                variant={eventFilter === 'upcoming' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setEventFilter('upcoming')}
                className="me-2"
              >
                Upcoming
              </Button>
              <Button
                variant={eventFilter === 'finished' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setEventFilter('finished')}
              >
                Finished
              </Button>
            </div>
            <div>
              <Badge bg="info" className="me-2">
                Total: {getFilteredEvents().length}
              </Badge>
              <Badge bg="success" className="me-2">
                Upcoming: {events.filter(e => e.start > new Date()).length}
              </Badge>
              <Badge bg="secondary">
                Finished: {events.filter(e => e.end < new Date()).length}
              </Badge>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'calendar' && (
        <div className="d-flex" style={{ gap: '20px' }}>
          <div style={{
            height: '70vh',
            borderRadius: '8px',
            overflow: 'hidden',
            flex: '3'
          }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              onNavigate={handleNavigate}
              onView={handleViewChange}
              view={view}
              date={currentDate}
              eventPropGetter={eventStyleGetter}
              components={{
                toolbar: () => null,
              }}
              style={{
                backgroundColor: 'white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            />
          </div>

          <div className='d-none' style={{
            flex: '1',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            overflowY: 'auto',
            maxHeight: '70vh'
            
          }}>
            <h5 className="mb-3">Events on {moment(currentDate).format('MMMM D, YYYY')}</h5>
            
            {selectedDateEvents.length > 0 ? (
              <ListGroup>
                {selectedDateEvents.map(event => (
                  <ListGroup.Item 
                    key={event.id} 
                    action 
                    onClick={() => handleSelectEvent(event)}
                    className="mb-2"
                    style={{
                      borderLeft: `4px solid ${
                        event.type === 'holiday' ? '#dc3545' : 
                        event.type === 'exam' ? '#ffc107' : '#0d6efd'
                      }`,
                      cursor: 'pointer'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{event.title}</strong>
                        {!event.allDay && (
                          <div className="text-muted small">
                            {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}
                          </div>
                        )}
                      </div>
                      <Badge 
                        pill 
                        bg={
                          event.type === 'holiday' ? 'danger' : 
                          event.type === 'exam' ? 'warning' : 'primary'
                        }
                        className="text-capitalize"
                      >
                        {event.type}
                      </Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <div className="text-muted">No events scheduled for this day</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          overflowY: 'auto',
          maxHeight: '70vh'
        }}>
          <ListGroup>
            {getFilteredEvents().length > 0 ? (
              getFilteredEvents()
                .sort((a, b) => a.start - b.start)
                .map(event => (
                  <ListGroup.Item 
                    key={event.id} 
                    action 
                    onClick={() => handleSelectEvent(event)}
                    className="mb-2"
                    style={{
                      borderLeft: `4px solid ${
                        event.type === 'holiday' ? '#dc3545' : 
                        event.type === 'exam' ? '#ffc107' : '#0d6efd'
                      }`,
                      cursor: 'pointer'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{event.title}</strong>
                        <div className="text-muted small">
                          {moment(event.start).format('MMMM D, YYYY')}
                          {!event.allDay && (
                            <span> • {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}</span>
                          )}
                        </div>
                        {event.location && (
                          <div className="text-muted small">
                            <i className="bi bi-geo-alt"></i> {event.location}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column align-items-end">
                        <Badge 
                          pill 
                          bg={
                            event.type === 'holiday' ? 'danger' : 
                            event.type === 'exam' ? 'warning' : 'primary'
                          }
                          className="text-capitalize mb-1"
                        >
                          {event.type}
                        </Badge>
                        <Badge 
                          pill 
                          bg={event.end < new Date() ? 'secondary' : 'success'}
                          className="text-capitalize"
                        >
                          {event.end < new Date() ? 'Finished' : 'Upcoming'}
                        </Badge>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))
            ) : (
              <div className="text-muted text-center py-4">No events found</div>
            )}
          </ListGroup>
        </div>
      )}

      <Modal show={showEventModal} onHide={() => setShowEventModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEvent?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div>
              <div className="mb-3">
                <Badge 
                  bg={
                    selectedEvent.type === 'holiday' ? 'danger' : 
                    selectedEvent.type === 'exam' ? 'warning' : 'primary'
                  }
                  className="text-capitalize me-2"
                >
                  {selectedEvent.type}
                </Badge>
                <Badge 
                  bg={selectedEvent.end < new Date() ? 'secondary' : 'success'}
                  className="text-capitalize"
                >
                  {selectedEvent.end < new Date() ? 'Finished' : 'Upcoming'}
                </Badge>
              </div>
              <p>
                <strong>Date:</strong> {moment(selectedEvent.start).format('LL')}
              </p>
              {!selectedEvent.allDay && (
                <p>
                  <strong>Time:</strong> {moment(selectedEvent.start).format('LT')} - {moment(selectedEvent.end).format('LT')}
                </p>
              )}
              {selectedEvent.location && (
                <p>
                  <strong>Location:</strong> {selectedEvent.location}
                </p>
              )}
              {selectedEvent.description && (
                <>
                  <strong>Details:</strong>
                  <p>{selectedEvent.description}</p>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEventModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="mt-4 p-3 bg-light rounded">
        <h5 className="mb-3">Calendar Legend</h5>
        <div className="d-flex flex-wrap gap-3">
          <div className="d-flex align-items-center">
            <div className="legend-color bg-primary me-2"></div>
            <span>School Events</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="legend-color bg-danger me-2"></div>
            <span>Holidays</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="legend-color bg-warning me-2"></div>
            <span>Exams</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="legend-color bg-success me-2"></div>
            <span>Upcoming</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="legend-color bg-secondary me-2"></div>
            <span>Finished</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 4px;
        }
        .school-calendar {
          max-width: 1200px;
          margin: 0 auto;
        }
        .rbc-event {
          cursor: pointer;
          transition: all 0.2s;
        }
        .rbc-event:hover {
          opacity: 1 !important;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default Calendarview;